import { useTranslation } from 'react-i18next';
import { GripVertical, Plus, FileText, List, Link, Code, User, CheckSquare, AlignLeft, Trash2, PenLine, Upload, Sparkles, Lock, Unlock } from 'lucide-react';
import { WorkspaceOverviewBlock, BlockType } from '../types/WorkspaceOverviewTypes';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { cn } from '@/infrastructure/lib/utils';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';

const blockTypeIcon = (type: BlockType) => {
  switch (type) {
    case 'text':
      return <AlignLeft className="w-3.5 h-3.5 shrink-0" />;
    case 'textarea':
    case 'rich_text':
      return <FileText className="w-3.5 h-3.5 shrink-0" />;
    case 'list':
      return <List className="w-3.5 h-3.5 shrink-0" />;
    case 'checklist':
      return <CheckSquare className="w-3.5 h-3.5 shrink-0" />;
    case 'link':
    case 'links':
      return <Link className="w-3.5 h-3.5 shrink-0" />;
    case 'code':
      return <Code className="w-3.5 h-3.5 shrink-0" />;
    case 'group':
      return <User className="w-3.5 h-3.5 shrink-0" />;
    default:
      return <FileText className="w-3.5 h-3.5 shrink-0" />;
  }
};

interface TreeItemProps {
  block: WorkspaceOverviewBlock;
  isSelected: boolean;
  isDirty: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onToggleLock: () => void;
}

const TreeItem = ({ block, isSelected, isDirty, canEdit, onSelect, onDelete, onToggleLock }: TreeItemProps) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
    disabled: !canEdit,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      title={block.description}
      className={cn(
        'group flex items-start gap-1.5 px-2 py-3 cursor-pointer rounded-sm select-none',
        isSelected
          ? 'bg-accent text-accent-foreground border-l-2 border-primary pl-[7px]'
          : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
      )}
      onClick={onSelect}
    >
      {canEdit ? (
        <button
          className="cursor-grab active:cursor-grabbing touch-none opacity-0 group-hover:opacity-100 shrink-0 mt-0.5"
          {...attributes}
          {...listeners}
          onClick={e => e.stopPropagation()}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
      ) : (
        <span className="w-3.5 shrink-0" />
      )}
      <div className="mt-0.5 shrink-0">{blockTypeIcon(block.block_type)}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="flex-1 truncate text-xs">{block.title}</span>
          <span className="font-mono text-[10px] text-muted-foreground shrink-0">{t(`knowledge.overview.block_types.${block.block_type}.short`, block.block_type)}</span>
          {/* Cadeado (AB#86817, AC6): trancado, o bloco não muda até ser destrancado. */}
          {(block.locked || canEdit) && (
            <button
              type="button"
              disabled={!canEdit}
              title={block.locked ? t('knowledge.mind.locked') : t('knowledge.mind.lock')}
              aria-label={block.locked ? t('knowledge.mind.unlock') : t('knowledge.mind.lock')}
              className={cn(
                'shrink-0',
                block.locked ? 'text-foreground' : 'opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground'
              )}
              onClick={(e) => { e.stopPropagation(); onToggleLock(); }}
            >
              {block.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
            </button>
          )}
          {/* Bloco trancado não se exclui pela tela (AB#86817): destranque antes. */}
          {canEdit && !block.locked && (
            <button
              className="opacity-0 group-hover:opacity-100 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full shrink-0',
              isDirty ? 'bg-yellow-500' : block.is_active ? 'bg-green-500' : 'bg-muted-foreground/40'
            )}
          />
        </div>
        {block.description && (
          <p className="text-[11px] text-muted-foreground truncate mt-0.5">{block.description}</p>
        )}
      </div>
    </div>
  );
};

interface Props {
  blocks: WorkspaceOverviewBlock[];
  selectedBlockId: string | null;
  dirtyBlockIds: Set<string>;
  canEdit: boolean;
  onSelect: (block: WorkspaceOverviewBlock) => void;
  onDelete: (blockId: string) => void;
  onToggleLock: (block: WorkspaceOverviewBlock) => void;
  onAdd: () => void;
  onUpload: () => void;
  onGenerate: () => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragStart: (event: DragStartEvent) => void;
  onDragCancel: () => void;
}

export const OverviewBlockTree = ({
  blocks,
  selectedBlockId,
  dirtyBlockIds,
  canEdit,
  onSelect,
  onDelete,
  onToggleLock,
  onAdd,
  onUpload,
  onGenerate,
  onDragEnd,
  onDragStart,
  onDragCancel,
}: Props) => {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <div className="w-[260px] shrink-0 flex flex-col border-r bg-muted/20 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('knowledge.overview.sections')}
        </span>
        {canEdit && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={onAdd}>
              <PenLine className="w-3.5 h-3.5 mr-2" />
              {t('knowledge.overview.write', 'Write')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onUpload}>
              <Upload className="w-3.5 h-3.5 mr-2" />
              {t('knowledge.overview.upload')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onGenerate} className="text-[#0B6FC2] focus:text-[#0B6FC2]">
              <Sparkles className="w-3.5 h-3.5 mr-2" />
              {t('knowledge.overview.generate_blocks')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-1">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
          onDragCancel={onDragCancel}
        >
          <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
            {blocks.map(block => (
              <TreeItem
                key={block.id}
                block={block}
                isSelected={block.id === selectedBlockId}
                isDirty={dirtyBlockIds.has(block.id)}
                canEdit={canEdit}
                onSelect={() => onSelect(block)}
                onDelete={() => onDelete(block.id)}
                onToggleLock={() => onToggleLock(block)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};
